// HR salary calculator — the pure arithmetic behind the Stipends page.
//
// Policy: the allocated salary is the baseline. Leave is a *deduction* from it,
// never an addition to it. The first PAID_LEAVE_ALLOWANCE leave days are paid in
// full; each day beyond that is loss of pay at the daily rate.

/** Leave days covered without cutting pay. Beyond this, each day reduces the stipend. */
export const PAID_LEAVE_ALLOWANCE = 2

export interface SalaryCalcInput {
  /** Inclusive YYYY-MM-DD bounds of the pay period. */
  startDate: string
  endDate: string
  allocatedSalary: number
  leavesTaken: number
  /** Manual deduction applied after the leave calculation. */
  reductions: number
  /** Days the person actually checked in — reported, not used to scale pay. */
  workedDays: number
}

export interface SalaryCalcResult {
  totalDays: number
  allocatedSalary: number
  leavesTaken: number
  reductions: number
  workedDays: number
  dailyRate: number
  paidLeaveDays: number
  unpaidLeaveDays: number
  leaveDeduction: number
  payableDays: number
  baseEarned: number
  totalPay: number
}

function nonNegative(value: unknown): number {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? n : 0
}

/** Inclusive day count between two YYYY-MM-DD dates; 0 if the range is invalid. */
export function daysInRange(startDate: string, endDate: string): number {
  if (!startDate || !endDate || startDate > endDate) return 0
  const start = new Date(`${startDate}T00:00:00`).getTime()
  const end = new Date(`${endDate}T00:00:00`).getTime()
  if (Number.isNaN(start) || Number.isNaN(end)) return 0
  return Math.floor((end - start) / 86_400_000) + 1
}

export function computeSalary(input: SalaryCalcInput): SalaryCalcResult {
  const totalDays = daysInRange(input.startDate, input.endDate)
  const allocatedSalary = nonNegative(input.allocatedSalary)
  const leavesTaken = nonNegative(input.leavesTaken)
  const reductions = nonNegative(input.reductions)
  const workedDays = nonNegative(input.workedDays)

  // Loss-of-pay rate: allocated salary spread over the calendar days in range.
  const dailyRate = totalDays > 0 ? allocatedSalary / totalDays : 0

  const paidLeaveDays = Math.min(PAID_LEAVE_ALLOWANCE, leavesTaken)
  const unpaidLeaveDays = Math.max(0, leavesTaken - PAID_LEAVE_ALLOWANCE)
  const leaveDeduction = unpaidLeaveDays * dailyRate
  const payableDays = Math.max(0, totalDays - unpaidLeaveDays)

  const baseEarned = Math.max(0, allocatedSalary - leaveDeduction)
  const totalPay = Math.max(0, baseEarned - reductions)

  return {
    totalDays, allocatedSalary, leavesTaken, reductions, workedDays,
    dailyRate, paidLeaveDays, unpaidLeaveDays, leaveDeduction,
    payableDays, baseEarned, totalPay,
  }
}
